import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import { QrCode2, Shield, TrendingUp } from '@mui/icons-material';

const colors = {
  cobaltDeep: '#1a2f6c',
  cobaltMain: '#2b4cc0',
  cobaltBright: '#5a7dff',
  cobaltSky: '#7d9fff',
  cobaltIce: '#b3c9ff',
  midnight: '#0f1a34',
  frost: '#f5f7ff',
};

const solutions = [
  {
    icon: QrCode2,
    number: '01',
    title: 'QR코드 기반 현장 데이터화',
    description:
      '모든 금형에 부착된 QR코드를 스마트폰으로 스캔하는 즉시, 모든 정보가 실시간으로 기록됩니다. "금형을 찾는 시간"과 "수기 입력 오류"를 원천적으로 해결합니다.',
  },
  {
    icon: Shield,
    number: '02',
    title: '실시간 수명 예측을 통한 사전 예방',
    description:
      '생산 정보(쇼트 수)가 자동으로 집계되어 금형의 잔여 수명을 실시간으로 계산하고, 위험 단계를 자동으로 알려줍니다. "갑작스러운 고장"으로 인한 생산 중단을 효과적으로 방지합니다.',
  },
  {
    icon: TrendingUp,
    number: '03',
    title: '데이터 중앙화를 통한 경영 가시성 확보',
    description:
      '엑셀과 서류에 흩어져 있던 모든 정보가 클라우드 기반 단일 플랫폼으로 통합됩니다. 경영진은 언제 어디서든 전체 금형 자산 현황을 한눈에 파악하고 전략적 의사결정을 내릴 수 있습니다.',
  },
];

const SolutionSection: React.FC = () => {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(165deg, ${colors.cobaltDeep}30 0%, ${colors.midnight} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '-10%',
          width: '40%',
          height: '40%',
          background: `radial-gradient(circle, ${colors.cobaltBright}20 0%, transparent 70%)`,
          filter: 'blur(100px)',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* 섹션 헤더 */}
        <Stack spacing={2} alignItems="center" textAlign="center" mb={8}>
          <Typography
            variant="overline"
            sx={{
              color: colors.cobaltBright,
              fontSize: '0.9rem',
              letterSpacing: '0.15em',
              fontWeight: 600,
            }}
          >
            해결책 제시
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: colors.frost,
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            스마트 팩토리의 시작, EGMMS
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.cobaltIce,
              maxWidth: '700px',
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.7,
            }}
          >
            현장의 모든 데이터를 귀사의 핵심 디지털 자산으로 전환하고,
            이를 기반으로 정확한 예측과 신속한 의사결정을 지원합니다.
          </Typography>
        </Stack>

        {/* Feature Abstract Image */}
        <Box
          sx={{
            mb: 8,
            borderRadius: '24px',
            overflow: 'hidden',
            border: `2px solid ${colors.cobaltBright}40`,
            boxShadow: `0 25px 60px ${colors.cobaltBright}20`,
          }}
        >
          <img
            src="/landing/feature-abstract.jpg"
            alt="EGMMS Smart Features"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Box>

        {/* 솔루션 카드 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {solutions.map((solution, index) => {
            const Icon = solution.icon;
            return (
              <Box key={index} sx={{ flex: '0 0 auto', width: { xs: '100%', md: 'calc(33.333% - 21.33px)' } }}>
                <Box
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: '24px',
                    background: `linear-gradient(135deg, ${colors.cobaltMain}20, ${colors.cobaltDeep}10)`,
                    backdropFilter: 'blur(20px)',
                    border: `2px solid ${colors.cobaltBright}40`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      borderColor: colors.cobaltBright,
                      boxShadow: `0 25px 50px ${colors.cobaltBright}30`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: 150,
                      height: 150,
                      background: `radial-gradient(circle, ${colors.cobaltBright}20, transparent 70%)`,
                      filter: 'blur(40px)',
                    },
                  }}
                >
                  <Stack spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
                    {/* 번호 */}
                    <Typography
                      sx={{
                        fontSize: '4rem',
                        fontWeight: 700,
                        color: colors.cobaltBright,
                        opacity: 0.3,
                        lineHeight: 1,
                      }}
                    >
                      {solution.number}
                    </Typography>

                    {/* 아이콘 */}
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${colors.cobaltBright}, ${colors.cobaltSky})`,
                        boxShadow: `0 8px 20px ${colors.cobaltBright}40`,
                      }}
                    >
                      <Icon sx={{ fontSize: 36, color: colors.frost }} />
                    </Box>

                    {/* 제목 */}
                    <Typography
                      variant="h5"
                      sx={{
                        color: colors.frost,
                        fontWeight: 600,
                        fontSize: '1.3rem',
                      }}
                    >
                      {solution.title}
                    </Typography>

                    {/* 설명 */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.cobaltIce,
                        lineHeight: 1.8,
                        fontSize: '0.95rem',
                      }}
                    >
                      {solution.description}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default SolutionSection;
